/**
 * Home's four cards, each one figure with its unit, a sentence for
 * its tooltip and the page it opens: Chat, Governance, Updates, Allowance.
 *
 * @module components/home/homeStatCards.logic
 */
import type { FindingItem } from "../regulation/finding/findingItem";
import { isUrgentFinding, type HomeFindingsStatus } from "./HomeFindings.logic";
import { agoInWords, pounds } from "./homeDates.logic";
import type { HomeAllowance, HomeGlance } from "./homeGlance.logic";

export type HomeStatCardKey = "chat" | "governance" | "updates" | "allowance";

export interface HomeStatCard {
  readonly key: HomeStatCardKey;
  readonly label: string;
  readonly value: string;
  /** In lower case, after the value: "open", "to review", "/ £90.00 used". */
  readonly unit: string;
  readonly detail: string;
  readonly to: "/$org/chat" | "/$org/compliance" | "/$org/compliance/regulatory" | "/$org/usage";
}

/** `resetsOn` is a calendar day; read at local noon so it never slips a day across a timezone. */
const resetDay = (resetsOn: string) =>
  new Date(`${resetsOn}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long" });

function allowanceFigure(allowance: HomeAllowance): Pick<HomeStatCard, "value" | "unit" | "detail"> {
  switch (allowance.kind) {
    case "shown": {
      const { spend } = allowance;
      return {
        value: pounds(spend.spentGbp),
        unit: `/ ${pounds(spend.includedGbp)} used`,
        detail: `${pounds(spend.spentGbp)} of your ${pounds(spend.includedGbp)} monthly allowance used. Resets ${resetDay(spend.resetsOn)}`,
      };
    }
    case "unlimited":
      return { value: pounds(allowance.spentGbp), unit: "used", detail: `${pounds(allowance.spentGbp)} used this month, with no monthly allowance` };
    case "unavailable":
      return { value: "…", unit: "unavailable", detail: "Your allowance could not be loaded" };
    case "loading":
      return { value: "…", unit: "", detail: "" };
  }
}

export function homeStatCards(input: {
  /** Chats running now, and chats stopped waiting on you. */
  readonly chat: { readonly working: number; readonly waiting: number };
  readonly findings: ReadonlyArray<FindingItem>;
  readonly findingsStatus: HomeFindingsStatus;
  readonly glance: Pick<HomeGlance, "toReview" | "lastChecked" | "allowance">;
  readonly now: number;
}): ReadonlyArray<HomeStatCard> {
  const { chat, findings, findingsStatus, glance, now } = input;
  const toReview = glance.toReview.length;
  const urgent = findings.filter(isUrgentFinding).length;
  return [
    {
      key: "chat",
      label: "Chat",
      value: String(chat.waiting > 0 ? chat.waiting : chat.working),
      unit: chat.waiting > 0 ? "waiting" : "working",
      detail: `${chat.working === 1 ? "One chat" : `${chat.working} chats`} working, ${chat.waiting === 0 ? "none" : chat.waiting} waiting for you`,
      to: "/$org/chat",
    },
    {
      key: "governance",
      label: "Governance",
      value: findingsStatus === "ready" ? String(findings.length) : "…",
      unit: findingsStatus === "unavailable" ? "unavailable" : findingsStatus === "ready" ? "open" : "",
      detail:
        findingsStatus === "unavailable"
          ? "Findings could not be loaded"
          : findingsStatus === "ready"
            ? `${findings.length} open findings, ${urgent} of them high or critical`
            : "",
      to: "/$org/compliance",
    },
    {
      key: "updates",
      label: "Updates",
      value: String(toReview),
      unit: "to review",
      detail: `${toReview === 0 ? "No regulatory updates" : toReview === 1 ? "One regulatory update" : `${toReview} regulatory updates`} waiting for your review${glance.lastChecked ? `. Checked ${agoInWords(glance.lastChecked, now)}` : ""}`,
      to: "/$org/compliance/regulatory",
    },
    {
      key: "allowance",
      label: "Allowance",
      ...allowanceFigure(glance.allowance),
      to: "/$org/usage",
    },
  ];
}
